import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dvmExtractorService from '../services/dvmExtractorService';
import ConfirmModal from './shared/ConfirmModal';
import './AccountForm.css';

// DVM Extractor — the saved set-ups. Superadmin only for now (the card is hidden on /apps and the
// lambda refuses anyone else, since hiding a card does not stop a URL being typed).

const DvmExtractorListPage = () => {
    const navigate = useNavigate();
    const [extractors, setExtractors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const data = await dvmExtractorService.getAll();
            setExtractors(data.dvmextractors || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error?.message || err.message || 'Could not load extractors');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleDelete = (row) => {
        setConfirmModal({
            title: 'Delete Extractor',
            message: `Delete "${row.name}"? Its extract history goes with it. This cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                try {
                    await dvmExtractorService.delete(row.id);
                    setExtractors(prev => prev.filter(e => e.id !== row.id));
                } catch (err) {
                    setError(err.response?.data?.error?.message || 'Could not delete');
                }
            },
        });
    };

    return (
        <div className="account-form-container">
            <div className="account-form-header">
                <h1>DVM Extractor</h1>
                <p className="no-data-hint" style={{ margin: '0.35rem 0 0' }}>
                    Pull a property’s Data Value Mappings out of OHIP and hand them back as a file.
                </p>
            </div>

            <div className="hotels-header">
                <button type="button" className="btn-add-hotel" onClick={() => navigate('/apps/dvm-extractor/new')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    New DVM Extractor
                </button>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Hotel</th>
                            <th>External System</th>
                            <th>Format</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="no-data"><p>Loading…</p></td></tr>
                        ) : extractors.length === 0 ? (
                            <tr><td colSpan="5" className="no-data">
                                <p>No extractors yet</p>
                                <p className="no-data-hint">Create one to extract a property’s DVM from OHIP</p>
                            </td></tr>
                        ) : (
                            extractors.map(row => (
                                <tr
                                    key={row.id}
                                    className="clickable-row"
                                    onClick={() => navigate(`/apps/dvm-extractor/${row.id}`)}
                                >
                                    <td className="primary-text">{row.name}</td>
                                    <td>{row.hotelname || row.hotel_id}</td>
                                    <td>{row.external_system}</td>
                                    <td>{String(row.format || '').toUpperCase()}</td>
                                    <td className="actions-cell">
                                        <button
                                            type="button"
                                            className="btn-action btn-delete-action"
                                            onClick={e => { e.stopPropagation(); handleDelete(row); }}
                                            title="Delete"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M3 4h10M6 4V2h4v2M5 4v9h6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {confirmModal && (
                <ConfirmModal
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmLabel={confirmModal.confirmLabel}
                    danger={confirmModal.danger}
                    onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

export default DvmExtractorListPage;
